import { useState, type FormEvent } from "react";
import { useAuth } from "../hooks/useAuth";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, Card, Mono } from "../components/v3";

interface LocationState {
  from?: { pathname: string };
}

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    const success = await login(password);
    setLoading(false);

    if (success) {
      const from = (location.state as LocationState | undefined)?.from?.pathname;
      navigate(from ?? "/app/home", { replace: true });
    } else {
      setError("Invalid password");
    }
  }

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to operate Vooice.">
      <form onSubmit={handleSubmit} className="space-y-4">
        <SsoButton provider="google" />

        <Divider>or</Divider>

        <div>
          <label className="mono-caps block mb-2" htmlFor="login-password">
            Password
          </label>
          <input
            id="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 bg-bg-2 border border-line rounded-md text-t1 placeholder:text-t4 focus:outline-none focus:border-amber/50 transition-colors"
            placeholder="Enter password"
            autoComplete="current-password"
            autoFocus
          />
        </div>

        {error && (
          <div className="text-rose text-sm border border-rose/30 bg-rose/5 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <Button
          type="submit"
          variant="amber"
          size="lg"
          disabled={loading || !password}
          className="w-full"
        >
          {loading ? "Signing in…" : "Sign in"}
        </Button>

        <div className="text-center pt-2">
          <span className="text-t3 text-sm">No account yet? </span>
          <Link to="/signup" className="text-amber hover:underline text-sm">
            Sign up
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}

// ── Shared shell for /login + /signup ──────────────────────────────────────

export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="stage" aria-hidden="true" />
      <div className="relative w-full max-w-[420px]">
        <Link to="/" className="inline-flex items-center gap-2.5 mb-10">
          <BrandMark />
          <span className="font-display text-2xl tracking-tight">Vooice</span>
        </Link>
        <h1 className="font-display text-3xl mb-2 leading-tight">{title}</h1>
        <p className="text-t2 mb-8">{subtitle}</p>
        <Card glass padding="lg">
          {children}
        </Card>
        <p className="mono-caps text-center mt-8">
          She introduces herself as AI · Every call · Every time
        </p>
      </div>
    </div>
  );
}

export function SsoButton({ provider }: { provider: "google" }) {
  // Wired up to real SSO once multi-user backend ships (task #63).
  // Until then, click is a no-op with an inline tooltip-style hint.
  return (
    <button
      type="button"
      disabled
      className="w-full inline-flex items-center justify-center gap-3 px-5 py-3 rounded-pill border border-line bg-white/[0.02] text-t2 text-sm hover:border-line-soft transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      title="Coming soon — single-user backend currently"
    >
      <GoogleIcon />
      <span className="font-medium">Continue with {capitalize(provider)}</span>
      <span className="mono-caps text-t4 ml-1">soon</span>
    </button>
  );
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function Divider({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="flex-1 h-px bg-line" />
      <Mono className="px-1">{children}</Mono>
      <div className="flex-1 h-px bg-line" />
    </div>
  );
}

function BrandMark() {
  return (
    <svg
      width="22"
      height="16"
      viewBox="0 0 56 40"
      fill="none"
      className="text-amber"
      aria-hidden="true"
    >
      <path
        d="M2 14 L10 26 L18 18 L26 30 L34 18 L42 26 L54 14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#FFB370"
        d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.614z"
      />
      <path
        fill="#FFB370"
        opacity="0.7"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.836.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FFB370"
        opacity="0.5"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#FFB370"
        opacity="0.85"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}
